/**
 * FeedbackPopup — Timed feedback survey that appears after 2-3 days
 *
 * Shows a modal asking users to rate different aspects of the platform
 * with a 5-star rating per category, plus a free-text "how can we make it better" field.
 *
 * Timing logic:
 * - On first login, records a timestamp in localStorage
 * - After 2+ days, shows the popup on next app open
 * - Once submitted or dismissed, records completion and won't show again
 *   (unless reset via admin or after a configurable cooldown)
 *
 * API_TODO:
 * - POST /api/feedback/submit { ratings, comment, submittedAt }
 * - GET /api/users/me → check feedbackSubmittedAt from backend
 */
import { useState } from 'react';
import { Icons } from './Icons';
import AscendingK from './AscendingK';

const FEEDBACK_CATEGORIES = [
  { key: 'ease_of_use', label: 'Ease of Use', description: 'How easy is it to navigate and use the platform?' },
  { key: 'profile_editor', label: 'Profile Editor', description: 'How well does the profile editor work for you?' },
  { key: 'qr_quality', label: 'QR Code Quality', description: 'How satisfied are you with your QR card?' },
  { key: 'scan_notifications', label: 'Scan Notifications', description: 'How useful are the scan notifications and CRM?' },
  { key: 'overall', label: 'Overall Experience', description: 'How would you rate Connekted in overall?' },
];

function StarRating({ value, onChange }) {
  const [hovered, setHovered] = useState(0);

  return (
    <div className="feedback-stars">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          className={`feedback-star ${star <= (hovered || value) ? 'filled' : ''}`}
          onClick={() => onChange(star)}
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          type="button"
        >
          <svg width="24" height="24" viewBox="0 0 24 24"
            fill={star <= (hovered || value) ? '#C9A961' : 'none'}
            stroke={star <= (hovered || value) ? '#C9A961' : 'currentColor'}
            strokeWidth="1.5"
          >
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        </button>
      ))}
    </div>
  );
}

export default function FeedbackPopup({ onSubmit, onDismiss }) {
  const [ratings, setRatings] = useState({});
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const filledCount = Object.keys(ratings).length;
  const allRated = filledCount === FEEDBACK_CATEGORIES.length;

  const handleRating = (key, value) => {
    setRatings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    const feedback = {
      id: `fb_${Date.now()}`,
      ratings,
      averageRating: Object.values(ratings).reduce((a, b) => a + b, 0) / filledCount,
      comment: comment.trim(),
      submittedAt: new Date().toISOString(),
    };

    // Mark as submitted in localStorage
    localStorage.setItem('connektedin_feedback_done', Date.now().toString());

    setSubmitted(true);
    setTimeout(() => {
      onSubmit(feedback);
    }, 2000);
  };

  const handleDismiss = () => {
    // Snooze for 1 more day by pushing the first-open forward
    const snoozeUntil = Date.now() + 24 * 60 * 60 * 1000;
    localStorage.setItem('connektedin_feedback_snooze', snoozeUntil.toString());
    onDismiss();
  };

  if (submitted) {
    return (
      <div className="feedback-overlay">
        <div className="feedback-modal">
          <div className="feedback-thankyou">
            <div className="feedback-thankyou-icon">{Icons.check}</div>
            <h2 className="feedback-thankyou-title">Thank you!</h2>
            <p className="feedback-thankyou-text">
              Your feedback helps us build a better experience for everyone.
            </p>
            <div className="feedback-footer-brand">
              <AscendingK size={12} color="#C9A961" />
              <span>connekted in</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="feedback-overlay">
      <div className="feedback-modal">
        {/* Header */}
        <div className="feedback-header">
          <div>
            <h2 className="feedback-title">We value your feedback</h2>
            <p className="feedback-subtitle">
              Help us improve Connekted in by rating your experience.
            </p>
          </div>
          <button className="feedback-close" onClick={handleDismiss}>
            {Icons.close}
          </button>
        </div>

        {/* Rating categories */}
        <div className="feedback-categories">
          {FEEDBACK_CATEGORIES.map((cat) => (
            <div key={cat.key} className="feedback-category">
              <div className="feedback-category-info">
                <div className="feedback-category-label">{cat.label}</div>
                <div className="feedback-category-desc">{cat.description}</div>
              </div>
              <StarRating
                value={ratings[cat.key] || 0}
                onChange={(val) => handleRating(cat.key, val)}
              />
            </div>
          ))}
        </div>

        {/* Comment field */}
        <div className="feedback-comment-section">
          <label className="feedback-comment-label">How can we make it better?</label>
          <textarea
            className="feedback-comment"
            placeholder="Tell us what you'd like to see improved, added, or changed..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
          />
        </div>

        {/* Footer */}
        <div className="feedback-actions">
          <button className="btn btn-ghost-app btn-sm" onClick={handleDismiss}>
            Maybe later
          </button>
          <button
            className="btn btn-primary-app btn-sm"
            onClick={handleSubmit}
            disabled={filledCount === 0}
          >
            Submit Feedback
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Check if the feedback popup should be shown.
 * Returns true if:
 * 1. User hasn't submitted feedback yet
 * 2. It's been at least 2 days since first app open
 * 3. Not currently snoozed
 *
 * API_TODO: Replace localStorage with backend user preferences
 */
export function shouldShowFeedback() {
  // Already submitted?
  if (localStorage.getItem('connektedin_feedback_done')) return false;

  // Currently snoozed?
  const snoozeUntil = localStorage.getItem('connektedin_feedback_snooze');
  if (snoozeUntil && Date.now() < parseInt(snoozeUntil, 10)) return false;

  // Check first-open timestamp
  const firstOpen = localStorage.getItem('connektedin_first_open');
  if (!firstOpen) {
    // Record first open now
    localStorage.setItem('connektedin_first_open', Date.now().toString());
    return false;
  }

  // Has 2 days passed? (172800000ms = 48 hours)
  const elapsed = Date.now() - parseInt(firstOpen, 10);
  return elapsed >= 2 * 24 * 60 * 60 * 1000;
}
